import type {
  CampaignData,
  PostcardResponse,
  CampaignStatsResponse,
  PostcardDesignOption,
} from '~~/layers/postcard/app/types'
import { TEXT_LIMITS } from '~~/layers/postcard/app/types'
import { usePostcardApi } from '~~/layers/postcard/app/composables/usePostcardApi'
import { email as formKitEmailRule } from '@formkit/rules'
import type { FormKitNode } from '@formkit/core'
import { sanitizeForCP850 } from '~~/layers/postcard/app/utils/encoding'
import { fetchImageAsFile } from '~~/layers/postcard/app/utils/fetchImageAsFile'
import { DEFAULT_QR_BRANDING } from '~~/layers/postcard/app/utils/postcardBranding'

const emptyPostcardData: CampaignData = {
  design: '',
  receiving: {
    firstname: '',
    lastname: '',
    street: '',
    number: '',
    city: '',
    postalCode: '',
  },
  message: '',
  sender: {
    firstname: '',
    lastname: '',
    street: '',
    number: '',
    city: '',
    postalCode: '',
    email: '',
  },
  consentPrivacy: false,
  consentMarketing: false,
  consentContest: false,
  branding: {
    brandingQRCode: {
      encodedText: '',
      accompanyingText: '',
      ...DEFAULT_QR_BRANDING,
    },
  },
}

const isValidEmail = (value: string | undefined | null): boolean => {
  if (!value) {
    return false
  }
  const mockNode = { value: value.trim() } as unknown as FormKitNode
  const result = formKitEmailRule(mockNode)
  return result instanceof Promise ? false : result
}

type StepValidationKey =
  'design' | 'receiving' | 'message' | 'sender' | 'review'

/**
 * Composable for managing campaign data across postcard steps
 * Provides direct access and update methods for campaign data
 * Handles API interactions and postcard lifecycle
 */
export const useCampaignData = () => {
  const { trackEvent } = useAnalytics()

  // Access the global campaign data state
  const campaignData = useState<CampaignData>('campaignData', () =>
    JSON.parse(JSON.stringify(emptyPostcardData)),
  )

  // Postcard state management
  const campaignKey = useState<string>('campaignKey', () => '')
  const campaignText = useState<string>('postcardCampaignText', () => '')
  const qrLink = useState<string>('postcardQrLink', () => '')
  const stampImageUrl = useState<string>('postcardStampImageUrl', () => '')
  const webformId = useState<string>('postcardWebformId', () => '')
  const postcard = useState<PostcardResponse | null>('postcard', () => null)
  const campaignStats = useState<CampaignStatsResponse | null>(
    'campaignStats',
    () => null,
  )
  const isApproved = useState<boolean>('postcardApproved', () => false)
  const designLocked = useState<boolean>('postcardDesignLocked', () => false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const stepValidationTriggered = useState<Record<StepValidationKey, boolean>>(
    'postcardStepValidationTriggered',
    () => ({
      design: false,
      receiving: false,
      message: false,
      sender: false,
      review: false,
    }),
  )

  // Initialize API composable
  const {
    createPostcard: apiCreatePostcard,
    uploadImage,
    setStampImage,
    getPostcardPreviewFront,
    getPostcardPreviewBack,
    updateSenderAddress,
    setSenderText,
    approvePostcard: apiApprovePostcard,
    getCampaignStats: apiGetCampaignStats,
    clearError: apiClearError,
  } = usePostcardApi({
    onError: (err) => {
      error.value = err.message || 'An error occurred'
      console.error('Postcard API Error:', err)
    },
    onSuccess: (msg) => {
      error.value = null
      console.log('Success:', msg)
    },
  })

  const designs = useState<PostcardDesignOption[]>('postcardDesigns', () => [])

  /**
   * Initialize campaign data
   * @param campaignKey
   */
  const setCampaignKey = (key: string) => {
    campaignKey.value = key
  }

  const setCampaignText = (text: string) => {
    campaignText.value = text
  }

  const setQrLink = (link: string) => {
    qrLink.value = link
  }

  const setStampImageUrl = (url: string) => {
    stampImageUrl.value = url
  }

  const setWebformId = (id: string) => {
    webformId.value = id
  }

  const setDesigns = (postcardDesigns: PostcardDesignOption[]) => {
    designs.value = postcardDesigns
  }

  const getSelectedDesign = (): PostcardDesignOption | undefined => {
    return designs.value.find(
      (design) => design.id === campaignData.value.design,
    )
  }

  const showStepValidationErrors = (step: StepValidationKey) => {
    if (stepValidationTriggered.value[step]) {
      return
    }

    stepValidationTriggered.value = {
      ...stepValidationTriggered.value,
      [step]: true,
    }
  }

  const clearStepValidationErrors = (step: StepValidationKey) => {
    if (!stepValidationTriggered.value[step]) {
      return
    }

    if (validateStep(step)) {
      stepValidationTriggered.value = {
        ...stepValidationTriggered.value,
        [step]: false,
      }
    }
  }

  /**
   * Create a postcard with current campaign data
   */
  const createPostcard = async () => {
    if (postcard.value) {
      console.log('Postcard already exists:', postcard.value.cardKey)
      return postcard.value
    }

    if (!campaignKey.value) {
      throw new Error('Cannot create postcard: campaignKey is not set')
    }

    if (!validateStep('receiving')) {
      showStepValidationErrors('receiving')
      throw new Error('Cannot create postcard: receiving data is incomplete')
    }

    isLoading.value = true
    try {
      const receiving = campaignData.value.receiving

      // Only recipient address is required for initial creation
      // Sender data and message will be added later via separate API calls
      const postcardData = await apiCreatePostcard(campaignKey.value, {
        recipientAddress: {
          firstname: receiving.firstname,
          lastname: receiving.lastname,
          street: receiving.street,
          houseNr: receiving.number,
          zip: receiving.postalCode,
          city: receiving.city,
        },
        branding: campaignData.value.branding,
      })

      if (!postcardData) {
        throw new Error('Failed to create postcard')
      }

      postcard.value = postcardData
      console.log('Postcard created successfully:', postcardData.cardKey)
      return postcardData
    } catch (err) {
      console.error('Failed to create postcard:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Approve and submit the postcard
   */
  const approvePostcard = async () => {
    if (!postcard.value) {
      throw new Error('No postcard to approve')
    }

    if (!validateStep('review')) {
      showStepValidationErrors('review')
      throw new Error('Cannot approve postcard: consent is required')
    }

    isLoading.value = true
    try {
      const success = await apiApprovePostcard(postcard.value.cardKey)

      if (!success) {
        throw new Error('Failed to approve postcard')
      }

      isApproved.value = true
      console.log('Postcard approved and submitted')

      await storePostcard()

      trackEvent('finish_postcard_contest')

      await resetCampaignData()
      console.log('Campaign data reset after approval')
      return true
    } catch (err) {
      console.error('Failed to approve postcard:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Upload postcard front image. Accepts a File or Drupal file URL
   * (URL is fetched in the browser, then sent as multipart to the middleware).
   */
  const uploadPostcardImage = async (image: File | string) => {
    if (!postcard.value) {
      throw new Error('No postcard exists to upload image to')
    }

    isLoading.value = true
    try {
      const file =
        typeof image === 'string' ? await fetchImageAsFile(image) : image
      const success = await uploadImage(postcard.value.cardKey, file)
      if (!success) {
        throw new Error(error.value || 'Image upload failed')
      }
      console.log('Image uploaded successfully')
    } catch (err) {
      console.error('Failed to upload image:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Upload stamp image. Accepts a File or Drupal file URL
   * (URL is fetched in the browser, then sent as multipart to the middleware).
   */
  const uploadPostcardStamp = async (stamp: File | string) => {
    if (!postcard.value) {
      throw new Error('No postcard exists to upload stamp image to')
    }

    isLoading.value = true
    try {
      const file =
        typeof stamp === 'string' ? await fetchImageAsFile(stamp) : stamp
      const success = await setStampImage(postcard.value.cardKey, file)
      if (!success) {
        throw new Error(error.value || 'Stamp image upload failed')
      }
      console.log('Stamp image uploaded successfully')
    } catch (err) {
      console.error('Failed to upload stamp image:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update postcard with sender information
   */
  const updatePostcardSender = async () => {
    if (!postcard.value) {
      throw new Error('No postcard exists to update sender information')
    }

    if (!validateStep('sender')) {
      showStepValidationErrors('sender')
      throw new Error('Cannot update sender: sender data is incomplete')
    }

    isLoading.value = true
    try {
      const sender = campaignData.value.sender

      // Update sender address via dedicated API method
      const success = await updateSenderAddress(postcard.value.cardKey, {
        firstname: sender.firstname,
        lastname: sender.lastname,
        street: sender.street,
        houseNr: sender.number,
        zip: sender.postalCode,
        city: sender.city,
      })

      if (!success) {
        throw new Error('Failed to update sender address')
      }

      console.log('Sender information updated successfully')
    } catch (err) {
      console.error('Failed to update sender information:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update postcard with message
   */
  const updatePostcardMessage = async () => {
    if (!postcard.value) {
      throw new Error('No postcard exists to update message')
    }

    if (!validateStep('message')) {
      showStepValidationErrors('message')
      throw new Error('Cannot update message: message is required')
    }

    isLoading.value = true
    try {
      // Sanitize message to make it compatible with 80s CP850 encoding.
      const { sanitized, removed } = sanitizeForCP850(
        campaignData.value.message,
      )

      // Log if characters were removed
      if (removed.length > 0) {
        console.warn(
          'Removed unsupported characters from message:',
          removed.join(', '),
        )
      }

      // Debug logging
      console.log('updatePostcardMessage - Debug info:', {
        cardKey: postcard.value.cardKey,
        originalMessage: campaignData.value.message,
        sanitizedMessage: sanitized,
        messageLength: sanitized.length,
        removedCharacters: removed,
      })

      // Update sender text/message via dedicated API method
      const success = await setSenderText(postcard.value.cardKey, sanitized)

      if (!success) {
        throw new Error('Failed to update sender text')
      }

      console.log('Message updated successfully')
    } catch (err) {
      console.error('Failed to update message:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update campaign data with partial data
   */
  const updateCampaignData = (updates: Partial<CampaignData>) => {
    campaignData.value = { ...campaignData.value, ...updates }
    console.log('Campaign data updated:', updates)
  }

  /**
   * Update specific nested fields
   */
  const updateReceiving = (updates: Partial<CampaignData['receiving']>) => {
    campaignData.value.receiving = {
      ...campaignData.value.receiving,
      ...updates,
    }
    clearStepValidationErrors('receiving')
  }

  const updateSender = (updates: Partial<CampaignData['sender']>) => {
    campaignData.value.sender = { ...campaignData.value.sender, ...updates }
    clearStepValidationErrors('sender')
  }

  const updateMessage = (message: string) => {
    campaignData.value.message = message
    clearStepValidationErrors('message')
  }

  const updateDesign = (design: string) => {
    if (designLocked.value && campaignData.value.design !== design) {
      console.warn('Ignored attempt to modify locked postcard design')
      return
    }

    console.log('Updating design to:', design)
    campaignData.value.design = design
    clearStepValidationErrors('design')
  }

  const updateConsent = (
    privacy?: boolean,
    marketing?: boolean,
    contest?: boolean,
  ) => {
    if (privacy !== undefined) {
      campaignData.value.consentPrivacy = privacy
    }
    if (marketing !== undefined) {
      campaignData.value.consentMarketing = marketing
    }
    if (contest !== undefined) {
      campaignData.value.consentContest = contest
    }
    clearStepValidationErrors('review')
  }

  /**
   * Reset campaign data to initial state
   */
  const resetCampaignData = async () => {
    campaignData.value = JSON.parse(JSON.stringify(emptyPostcardData))
    postcard.value = null
    isApproved.value = false
    designLocked.value = false
    error.value = null
    stepValidationTriggered.value = {
      design: false,
      receiving: false,
      message: false,
      sender: false,
      review: false,
    }
  }

  /**
   * Validate campaign data for current step
   */
  const validateStep = (stepName: string): boolean => {
    switch (stepName) {
      case 'design':
        return !!campaignData.value.design

      case 'receiving': {
        const receiving = campaignData.value.receiving
        return !!(
          receiving.firstname &&
          receiving.lastname &&
          receiving.street &&
          receiving.city &&
          receiving.postalCode
        )
      }

      case 'message':
        return (
          !!campaignData.value.message &&
          campaignData.value.message.trim().length > 0 &&
          campaignData.value.message.length <= TEXT_LIMITS.SENDER_TEXT
        )

      case 'sender': {
        const sender = campaignData.value.sender
        return !!(
          sender.firstname?.trim() &&
          sender.lastname?.trim() &&
          sender.street?.trim() &&
          sender.postalCode?.trim() &&
          sender.city?.trim() &&
          isValidEmail(sender.email)
        )
      }

      case 'review':
        return (
          campaignData.value.consentPrivacy && campaignData.value.consentContest
        )

      default:
        return true
    }
  }

  /**
   * Execute step-specific logic with API calls
   */
  const executeStepLogic = async (stepName: string): Promise<boolean> => {
    try {
      switch (stepName) {
        case '10-design':
          console.log('Design step: validating design selection')
          if (!campaignData.value.design) {
            console.error('Please select a design')
            return false
          }
          designLocked.value = true
          console.log(
            'Postcard design locked at value:',
            campaignData.value.design,
          )
          return true

        case '20-receiving':
          console.log(
            'Receiving step: validating recipient data and creating postcard',
          )
          if (!validateStep('receiving')) {
            showStepValidationErrors('receiving')
            console.error('Missing required recipient information')
            return false
          }

          // Create postcard if it doesn't exist yet
          if (!postcard.value) {
            await createPostcard()
          }
          return true

        case '30-message':
          console.log('Message step: validating message')
          if (!validateStep('message')) {
            showStepValidationErrors('message')
            console.error('Message is required')
            return false
          }

          // Update postcard with message if postcard exists
          if (postcard.value) {
            await updatePostcardMessage()
          }
          return true

        case '40-sender':
          console.log('Sender step: validating sender data')
          if (!validateStep('sender')) {
            showStepValidationErrors('sender')
            console.error('Missing required sender information')
            return false
          }

          // Update postcard with sender information if postcard exists
          if (postcard.value) {
            await updatePostcardSender()
          }
          return true

        case '50-review':
          console.log('Review step: validating and submitting postcard')
          if (!validateStep('review')) {
            showStepValidationErrors('review')
            console.error('Privacy consent is required')
            return false
          }
          // Approve and submit the postcard in the review step
          await approvePostcard()
          return true

        default:
          return true
      }
    } catch (err) {
      console.error(`Step ${stepName} failed:`, err)
      return false
    }
  }

  /**
   * Fetch campaign statistics
   */
  const fetchCampaignStats = async (campaignKey: string) => {
    if (!campaignKey) {
      throw new Error('campaignKey is not set')
    }
    isLoading.value = true
    try {
      const stats = await apiGetCampaignStats(campaignKey)
      campaignStats.value = stats
      console.log('Campaign stats fetched successfully:', stats)
      return stats
    } catch (err) {
      console.error('Failed to fetch campaign stats:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get postcard front preview
   */
  const getPostcardFrontPreview = async () => {
    if (!postcard.value) {
      throw new Error('No postcard exists to get preview for')
    }

    isLoading.value = true
    try {
      const preview = await getPostcardPreviewFront(postcard.value.cardKey)
      console.log('Front preview fetched successfully')
      return preview
    } catch (err) {
      console.error('Failed to fetch front preview:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get postcard back preview
   */
  const getPostcardBackPreview = async () => {
    if (!postcard.value) {
      throw new Error('No postcard exists to get preview for')
    }

    isLoading.value = true
    try {
      const preview = await getPostcardPreviewBack(postcard.value.cardKey)
      console.log('Back preview fetched successfully')
      return preview
    } catch (err) {
      console.error('Failed to fetch back preview:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const setBranding = (branding: CampaignData['branding']) => {
    campaignData.value.branding = branding
    console.log('Branding updated successfully:', branding)
  }

  const storePostcard = async () => {
    try {
      if (!webformId.value) {
        throw new Error('Cannot store postcard: webformId is not set')
      }

      const selectedDesign = getSelectedDesign()
      const postcardImage =
        selectedDesign?.imageUrl?.split('/').pop()?.split('?')[0] ||
        selectedDesign?.id ||
        ''

      // Sanitize message for webform to match what was sent to Swiss Post API
      const { sanitized: sanitizedMessage } = sanitizeForCP850(
        campaignData.value.message,
      )

      await useGraphqlMutation('submitWebform', {
        id: webformId.value,
        elements: [
          {
            element: 'card_key',
            value: postcard.value?.cardKey,
          },
          {
            element: 'card_design',
            value: postcardImage,
          },
          {
            element: 'message',
            value: sanitizedMessage,
          },
          {
            element: 'recipient_firstname',
            value: campaignData.value.receiving.firstname,
          },
          {
            element: 'recipient_lastname',
            value: campaignData.value.receiving.lastname,
          },
          {
            element: 'recipient_street',
            value: campaignData.value.receiving.street,
          },
          {
            element: 'recipient_number',
            value: campaignData.value.receiving.number,
          },
          {
            element: 'recipient_postal_code',
            value: campaignData.value.receiving.postalCode,
          },
          {
            element: 'recipient_city',
            value: campaignData.value.receiving.city,
          },
          {
            element: 'sender_firstname',
            value: campaignData.value.sender.firstname,
          },
          {
            element: 'sender_lastname',
            value: campaignData.value.sender.lastname,
          },
          {
            element: 'sender_street',
            value: campaignData.value.sender.street,
          },
          {
            element: 'sender_number',
            value: campaignData.value.sender.number,
          },
          {
            element: 'sender_postal_code',
            value: campaignData.value.sender.postalCode,
          },
          {
            element: 'sender_city',
            value: campaignData.value.sender.city,
          },
          {
            element: 'sender_email',
            value: campaignData.value.sender.email,
          },
          {
            element: 'consent_privacy',
            value: campaignData.value.consentPrivacy ? '1' : '0',
          },
          {
            element: 'consent_marketing',
            value: campaignData.value.consentMarketing ? '1' : '0',
          },
          {
            element: 'consent_contest',
            value: campaignData.value.consentContest ? '1' : '0',
          },
        ],
        files: [],
        sourceEntityType: '',
        sourceEntityId: '',
      })
      console.log('Postcard data stored via webform')
    } catch (error) {
      console.error('Failed to store postcard data via webform:', error)
      throw error
    }
  }

  return {
    // Data
    campaignKey: readonly(campaignKey),
    campaignText: readonly(campaignText),
    qrLink: readonly(qrLink),
    stampImageUrl: readonly(stampImageUrl),
    webformId: readonly(webformId),
    campaignData: readonly(campaignData),
    designs: readonly(designs),
    postcard: readonly(postcard),
    campaignStats: readonly(campaignStats),
    isLoading: readonly(isLoading),
    error: readonly(error),
    isApproved: readonly(isApproved),
    designLocked: readonly(designLocked),
    stepValidationTriggered: readonly(stepValidationTriggered),

    // Data updates
    setCampaignKey,
    setCampaignText,
    setQrLink,
    setStampImageUrl,
    setWebformId,
    setDesigns,
    getSelectedDesign,
    updateCampaignData,
    updateReceiving,
    updateSender,
    updateMessage,
    updateDesign,
    updateConsent,
    resetCampaignData,

    // Validation
    validateStep,

    // API actions
    createPostcard,
    approvePostcard,
    uploadPostcardImage,
    uploadPostcardStamp,
    updatePostcardSender,
    updatePostcardMessage,
    fetchCampaignStats,
    getPostcardFrontPreview,
    getPostcardBackPreview,
    executeStepLogic,
    setBranding,
    showStepValidationErrors,

    // Utility
    clearError: () => {
      error.value = null
      apiClearError()
    },
  }
}
